/**
 * Admin Payouts Tab
 * View creator payouts by period, filter by status, retry failed transfers
 */

import { LoadingSpinner } from '@components/ui';
import { type AdminPayout, useAdminPayouts } from '@hooks/useAdminPayouts';
import { ChevronLeft, ChevronRight, DollarSign, RefreshCw } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';
import { formatCents, PAYOUT_STATUS_LABELS, PAYOUT_STATUS_STYLES } from './utils/adminFormatters';

// =============================================================================
// Helpers
// =============================================================================

/** Get default period label (previous month in YYYY-MM) */
function getDefaultPeriodLabel(): string {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
}

// =============================================================================
// Component
// =============================================================================

export function PayoutsTab(): ReactElement {
  const [periodLabel, setPeriodLabel] = useState(getDefaultPeriodLabel);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { payouts, total, pages, loading, error, refetch, retryFailed, retrying } = useAdminPayouts(
    { periodLabel, status: statusFilter, page, limit },
  );

  // Summary stats for current results
  const summary = useMemo(() => {
    const totalAmount = payouts.reduce((sum, p) => sum + p.creatorShareCents, 0);
    const transferred = payouts.filter((p) => p.status === 'transferred').length;
    const failed = payouts.filter((p) => p.status === 'failed').length;
    const pending = payouts.filter((p) => p.status === 'calculated').length;
    return { totalAmount, transferred, failed, pending };
  }, [payouts]);

  const hasFailed = payouts.some((p) => p.status === 'failed');
  const [retryError, setRetryError] = useState<string | null>(null);

  async function handleRetry(): Promise<void> {
    setRetryError(null);
    const result = await retryFailed(periodLabel);
    if (!result.success) {
      setRetryError(result.error ?? 'Unknown error');
    }
  }

  // Period navigation
  function adjustPeriod(delta: number): void {
    const [year, month] = periodLabel.split('-').map(Number);
    const d = new Date(year, month - 1 + delta, 1);
    setPeriodLabel(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setPage(1);
  }

  const statusFilters = ['all', 'calculated', 'transferred', 'failed'];

  return (
    <div className="space-y-6">
      {/* Controls Row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Period Selector */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => adjustPeriod(-1)}
            className="p-1.5 rounded hover:bg-theme-surface text-theme-text-secondary"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-theme-text min-w-[80px] text-center">
            {periodLabel}
          </span>
          <button
            type="button"
            onClick={() => adjustPeriod(1)}
            className="p-1.5 rounded hover:bg-theme-surface text-theme-text-secondary"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Status Filters */}
        <div className="flex gap-2">
          {statusFilters.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setStatusFilter(s);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                  : 'bg-theme-surface text-theme-text-secondary hover:text-theme-text'
              }`}
            >
              {s === 'all' ? 'All' : (PAYOUT_STATUS_LABELS[s] ?? s)}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-theme-surface text-theme-text-secondary hover:text-theme-text"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {hasFailed && (
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50"
            >
              {retrying ? 'Retrying...' : 'Retry All Failed'}
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Total Payouts" value={String(total)} />
        <SummaryCard label="Total Amount" value={formatCents(summary.totalAmount)} icon />
        <SummaryCard
          label="Transferred"
          value={String(summary.transferred)}
          color="text-green-500"
        />
        <SummaryCard label="Failed" value={String(summary.failed)} color="text-red-500" />
      </div>

      {/* Errors */}
      {(error || retryError) && (
        <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4 text-theme-danger text-sm">
          {error || `Retry failed: ${retryError}`}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : payouts.length === 0 ? (
        <div className="text-center py-12 text-theme-text-tertiary">
          No payouts found for {periodLabel}
          {statusFilter !== 'all'
            ? ` with status "${PAYOUT_STATUS_LABELS[statusFilter] ?? statusFilter}"`
            : ''}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-theme-surface">
                <tr>
                  <th className="text-left p-3 text-theme-text-secondary font-medium">Creator</th>
                  <th className="text-left p-3 text-theme-text-secondary font-medium">Period</th>
                  <th className="text-right p-3 text-theme-text-secondary font-medium">Amount</th>
                  <th className="text-center p-3 text-theme-text-secondary font-medium">Status</th>
                  <th className="text-right p-3 text-theme-text-secondary font-medium">
                    Followers
                  </th>
                  <th className="text-left p-3 text-theme-text-secondary font-medium">
                    Transfer ID
                  </th>
                  <th className="text-left p-3 text-theme-text-secondary font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <PayoutRow key={payout.id} payout={payout} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between text-sm text-theme-text-secondary">
              <span>
                Page {page} of {pages} ({total} total)
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 rounded bg-theme-surface disabled:opacity-50 hover:bg-theme-border"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="px-3 py-1.5 rounded bg-theme-surface disabled:opacity-50 hover:bg-theme-border"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Sub-Components
// =============================================================================

function SummaryCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: string;
  color?: string;
  icon?: boolean;
}): ReactElement {
  return (
    <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
      <p className="text-xs text-theme-text-tertiary">{label}</p>
      <p className={`text-xl font-bold ${color ?? 'text-theme-text'} flex items-center gap-1`}>
        {icon && <DollarSign className="w-4 h-4" />}
        {value}
      </p>
    </div>
  );
}

function PayoutRow({ payout }: { payout: AdminPayout }): ReactElement {
  return (
    <tr className="border-t border-theme-border hover:bg-theme-surface/50">
      <td className="p-3">
        <div>
          <p className="text-theme-text font-medium">{payout.creatorName}</p>
          <p className="text-xs text-theme-text-tertiary">{payout.creatorEmail}</p>
        </div>
      </td>
      <td className="p-3 text-theme-text">{payout.periodLabel}</td>
      <td className="p-3 text-right font-mono text-theme-text">
        {formatCents(payout.creatorShareCents)}
      </td>
      <td className="p-3 text-center">
        <span
          className={`inline-block px-2 py-1 rounded text-xs font-medium ${PAYOUT_STATUS_STYLES[payout.status] ?? ''}`}
        >
          {PAYOUT_STATUS_LABELS[payout.status] ?? payout.status}
        </span>
      </td>
      <td className="p-3 text-right text-theme-text-secondary">
        {payout.eligibleFollowers}/{payout.totalFollowers}
      </td>
      <td className="p-3 text-xs font-mono text-theme-text-tertiary truncate max-w-[140px]">
        {payout.stripeTransferId ?? '—'}
      </td>
      <td
        className="p-3 text-xs text-red-500 truncate max-w-[200px]"
        title={payout.transferError ?? undefined}
      >
        {payout.transferError ?? '—'}
      </td>
    </tr>
  );
}
